import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Trophy, RotateCcw } from 'lucide-react';
import { CUP_PROGRESS_LABEL_DEFAULTS } from '../../lib/cupProgressLabels';

/**
 * Cup Progress Labels — lets the coach customise the badge + footer text
 * shown on Cup Progress widgets (Season Overview + Dashboard). Each entry
 * accepts a `{round}` placeholder that the widget swaps for the live round
 * name at render time (e.g. "Final", "Quarter Final").
 *
 * Persists to `user.cup_progress_labels` via PUT /api/settings.
 */
const FIELDS = [
  { key: 'active_badge',      label: 'In-Progress badge',  hint: 'Shown while the cup is still running.' },
  { key: 'active_footer',     label: 'In-Progress footer', hint: 'Use {round} to insert the current round.' },
  { key: 'eliminated_badge',  label: 'Eliminated badge',   hint: 'Shown after a knockout loss.' },
  { key: 'eliminated_footer', label: 'Eliminated footer',  hint: 'Use {round} to insert the round of exit.' },
  { key: 'winner_badge',      label: 'Winner badge',       hint: 'Shown after winning the Final.' },
  { key: 'winner_footer',     label: 'Winner footer',      hint: 'Use {round} to insert the round (usually "Final").' },
];

export function CupProgressLabelsSection({ cupProgressLabels, setCupProgressLabels }) {
  const value = cupProgressLabels || {};

  const updateField = (key, v) => {
    setCupProgressLabels({ ...value, [key]: v });
  };

  const resetField = (key) => {
    const next = { ...value };
    delete next[key];
    setCupProgressLabels(next);
  };

  const resetAll = () => setCupProgressLabels({});

  return (
    <Card data-testid="cup-progress-labels-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          Cup Progress Labels
        </CardTitle>
        <CardDescription>
          Customise the wording shown on the Cup Progress widget. Use <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">{'{round}'}</code> in footer text to insert the live round name.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {FIELDS.map(({ key, label, hint }) => {
          const fallback = CUP_PROGRESS_LABEL_DEFAULTS[key];
          const current = value[key];
          const isCustom = current !== undefined && current !== fallback;
          return (
            <div key={key} className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor={`cup-label-${key}`} className="text-sm font-medium">{label}</Label>
                {isCustom && (
                  <button
                    type="button"
                    onClick={() => resetField(key)}
                    className="text-[11px] text-gray-500 hover:text-gray-800 underline flex items-center gap-1"
                    data-testid={`cup-label-${key}-reset`}
                  >
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                )}
              </div>
              <Input
                id={`cup-label-${key}`}
                value={current ?? fallback}
                onChange={(e) => updateField(key, e.target.value)}
                placeholder={fallback}
                data-testid={`cup-label-${key}`}
              />
              <p className="text-xs text-gray-500">{hint}</p>
            </div>
          );
        })}

        <div className="pt-2 border-t border-gray-100">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={resetAll}
            data-testid="cup-labels-reset-all"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset all to defaults
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default CupProgressLabelsSection;
